import { z } from 'zod';
import { MoneyStringSchema } from './money';

/** Telegram 财务推送（docs/11-推荐分润与财务推送.md §3）。 */

export const TELEGRAM_EVENTS = ['daily_digest', 'quota_ops', 'large_topup', 'commission_settlement', 'critical_alerts', 'system_health'] as const;
export type TelegramEvent = (typeof TELEGRAM_EVENTS)[number];

export const TELEGRAM_EVENT_LABEL: Record<TelegramEvent, string> = {
  daily_digest: '每日财务日报',
  quota_ops: '运营加额 / 减额',
  large_topup: '大额充值',
  commission_settlement: '推荐佣金结算',
  critical_alerts: '严重告警',
  system_health: '系统健康预警',
};

/** 推送消息使用的时区（消息里的时间、日报发送时间与统计的“自然日”）；未设置时跟随站点时区 */
export const TELEGRAM_TIMEZONES = ['Asia/Shanghai', 'UTC', 'Asia/Hong_Kong', 'Asia/Singapore', 'Asia/Seoul', 'Asia/Ho_Chi_Minh', 'Europe/London', 'America/Los_Angeles', 'America/New_York'] as const;
export type TelegramTimezone = (typeof TELEGRAM_TIMEZONES)[number];
export const TELEGRAM_TIMEZONE_LABEL: Record<TelegramTimezone, string> = {
  'Asia/Shanghai': '北京时间（UTC+8）',
  UTC: 'UTC（协调世界时）',
  'Asia/Hong_Kong': '香港时间（UTC+8）',
  'Asia/Singapore': '新加坡时间（UTC+8）',
  'Asia/Seoul': '首尔时间（UTC+9）',
  'Asia/Ho_Chi_Minh': '越南时间（UTC+7）',
  'Europe/London': '伦敦时间',
  'America/Los_Angeles': '美国太平洋时间',
  'America/New_York': '美国东部时间',
};

/** 数字 chat id（群组为负数）或 @频道用户名 */
export const TelegramChatIdSchema = z
  .string()
  .trim()
  .regex(/^(-?\d{1,20}|@[A-Za-z][A-Za-z0-9_]{4,31})$/, 'chat id 应为数字（群组以 - 开头）或 @频道名');

export const TelegramEventsSchema = z.object({
  daily_digest: z.boolean(),
  quota_ops: z.boolean(),
  large_topup: z.boolean(),
  commission_settlement: z.boolean(),
  critical_alerts: z.boolean(),
  // 后加的事件给默认值：已保存的旧设置里没有这一项，解析时按开启处理
  system_health: z.boolean().default(true),
});

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export const TelegramDeliverySchema = z.object({
  at: z.string(),
  ok: z.boolean(),
  event: z.string(),
  chat_id: z.string(),
  error: z.string().nullable(),
});
export type TelegramDelivery = z.infer<typeof TelegramDeliverySchema>;

/** 管理端读取：机器人 Token 只返回指纹 */
export const TelegramSettingsSchema = z.object({
  enabled: z.boolean(),
  bot_token_set: z.boolean(),
  bot_token_fingerprint: z.string().nullable(),
  chat_ids: z.array(z.string()),
  events: TelegramEventsSchema,
  /** 日报发送时间（站点时区 HH:MM），汇总前一自然日 */
  digest_time: z.string().regex(HHMM),
  /** 单笔加额达到该金额视为“大额充值” */
  large_topup_threshold: MoneyStringSchema,
  /** 实际使用的时区（设置了消息时区用设置值，否则为站点时区） */
  timezone: z.string(),
  /** 后台设置的消息时区；null = 跟随站点时区 */
  timezone_setting: z.enum(TELEGRAM_TIMEZONES).nullable(),
  site_timezone: z.string(),
  last_delivery: TelegramDeliverySchema.nullable(),
  last_digest_day: z.string().nullable(),
});
export type TelegramSettings = z.infer<typeof TelegramSettingsSchema>;

export const UpdateTelegramSettingsSchema = z.object({
  enabled: z.boolean(),
  /** 省略 = 保持不变；null = 清除；字符串 = 更换 */
  bot_token: z
    .string()
    .trim()
    .regex(/^\d{5,20}:[A-Za-z0-9_-]{20,80}$/, 'Bot Token 格式应为 123456:ABC…')
    .nullable()
    .optional(),
  chat_ids: z.array(TelegramChatIdSchema).max(10),
  events: TelegramEventsSchema,
  digest_time: z.string().regex(HHMM, '时间格式 HH:MM'),
  large_topup_threshold: MoneyStringSchema,
  /** 省略 = 保持不变；null = 跟随站点时区 */
  timezone: z.enum(TELEGRAM_TIMEZONES).nullable().optional(),
});
export type UpdateTelegramSettings = z.infer<typeof UpdateTelegramSettingsSchema>;

export const TelegramTestResultSchema = z.object({
  results: z.array(z.object({ chat_id: z.string(), ok: z.boolean(), error: z.string().nullable() })),
});
export type TelegramTestResult = z.infer<typeof TelegramTestResultSchema>;
